import React, { useContext, useEffect, useMemo, useState } from "react";
import { Field, FormArray, FormGroup, GenericComponent, PInput } from "../../models/models";
import { AdvisoryText } from "../../../AdvisoryText";
import { FormContext } from "../../context/formContext";
import { ErrorMessage, useFormikContext } from "formik";
import FieldComponent from "./FieldComponent";
import FormArrayComponent from "./FormArrayComponent";

export default function FormGroupCompnent({
  parentKey,
  elementKey,
  label,
  heading,
  classNameOpt,
  warn,
  parentValues,
  hideExpression,
  disabledExpression,
  customWrapper,
  warnType,
  fields,
  rest,
  test,
}: {
  parentKey: string;
  elementKey?: string;
  label?: string;
  warn?: string;
  classNameOpt?: string;
  heading?: string;
  parentValues: any;
  hideExpression?: (formContext: any, values: any, model: any) => boolean;
  disabledExpression?: (formContext: any, values: any, model: any) => boolean;
  warnType?: "warning" | "information" | "error" | "announcement" | "fieldadv" | "sectionHeader";
  customWrapper?: React.FC<any>;
  fields: (Field | FormGroup | FormArray | PInput | GenericComponent)[];
  test?: [nameString: string, errorText: string, valueFunction: (value: any) => boolean];
  rest: any;
}) {
  const { values, setFieldValue } = useFormikContext();

  const context = useContext(FormContext);

  const [disabled, setDisabled] = useState(false);

  const CustomWrapper = customWrapper;
  const nextParentKey = useMemo(() => {
    return [parentKey, elementKey].filter((x) => x !== "" && x != null).join(".");
  }, [parentKey, elementKey]);

  const nextParentValues = useMemo(() => {
    const value = elementKey != null && elementKey != "" ? parentValues[elementKey] : parentValues;
    return value;
  }, [elementKey, parentValues]);

  useEffect(() => {
    if (disabledExpression != null) {
      const res = disabledExpression(context.formContextStore, values, parentValues);
      if (res != disabled) {
        setDisabled(res);
      }
    }

    if (hideExpression != null) {
      const isHidden = hideExpression(context.formContextStore, values, parentValues);
      // Only update the state if `isHidden` differs from `hidden`
      if (isHidden !== parentValues[elementKey + "Hidden"]) {
        setFieldValue(nextParentKey + "Hidden", isHidden);
      }
    }
  }, [hideExpression, disabledExpression, values, parentValues, context.formContextStore, disabled]);

  const hidden = useMemo(() => parentValues[elementKey + "Hidden"], [parentValues[elementKey + "Hidden"]]);

  useEffect(() => {
    if (test != null) {
      setFieldValue(nextParentKey + "Test", true);
    }
  }, []);

  const fieldsOutput = () => {
    return fields.map((f) => {
      switch (f.type) {
        case "formGroup":
          f = f as FormGroup;
          return (
            <FormGroupCompnent
              key={f.key}
              parentKey={nextParentKey}
              parentValues={nextParentValues}
              elementKey={f.key}
              label={f.label}
              fields={f.fields}
              hideExpression={f.hideExpression}
              customWrapper={f.customWrapper}
              classNameOpt={f.classNameOpt}
              heading={f.heading}
              test={f.test}
              rest={f.props}
            />
          );
        case "formArray":
          f = f as FormArray;
          return (
            <FormArrayComponent
              key={f.key}
              parentKey={nextParentKey}
              parentValues={nextParentValues}
              rest={f.props}
              elementKey={f.key}
              label={f.label}
              fields={f.fields}
              hideExpression={f.hideExpression}
              childModel={f.childModel}
              mutateValues={f.mutateValues}
              config={f.config}
            />
          );
        case "field":
          f = f as Field;
          return (
            <>
              <FieldComponent
                key={f.key}
                parentKey={nextParentKey}
                parentValues={nextParentValues}
                elementKey={f.key}
                fieldType={f.fieldType}
                label={f.label}
                extendedLabel={f.extendedLabel}
                warn={f.warn}
                rest={f.props}
                expressionProps={f.expressionProps}
                hideExpression={f.hideExpression}
                disabled={disabled}
                calculatedValue={f.calculatedValue}
              />
              <div className={f.props?.advisorytextcss}>{f.props?.advisorytext}</div>
            </>
          );

        case "pInput":
          f = f as PInput;
          return <div className={f.props?.css}>{f.label}</div>;
          break;
        case "component": {
          f = f as GenericComponent;
          const Body = f.body;
          return <Body values={values} />;
        }
      }
    });
  };

  return (
    <>
      {!hidden && (
        <>
          {CustomWrapper != null ? (
            <CustomWrapper>{fieldsOutput()}</CustomWrapper>
          ) : (
            <div className={classNameOpt}>
              {(heading || warn || label) && (
                <div>
                  {heading && (
                    <h4 className="text-purple mb-0 flex align-items-center border-bottom-2">
                      <span>{heading}</span>
                    </h4>
                  )}
                  {label && <label className={rest?.labelCss + (rest?.isRequired ? " isRequired" : "")}>{label}</label>}
                  {warn && <AdvisoryText type={warnType ?? "warn"} label={warn} />}
                </div>
              )}
              {fieldsOutput()}

              {nextParentKey && (
                <ErrorMessage name={nextParentKey + "Test"} render={(msg) => <div className="error">{typeof msg == "string" && msg}</div>} />
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
